import { useEffect } from 'react';
import * as THREE from 'three';
import { useAppStore } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { HERO_POSITIONS } from '../heroConstants';

export interface ViewportKeyboardRefs {
  controlsRef: React.RefObject<any>;
  isDrawingRef: React.MutableRefObject<boolean>;
  cameraRef: React.RefObject<THREE.Camera | null>;
  syncCamRef: React.MutableRefObject<() => void>;
  adjustBrushSizeRef: React.MutableRefObject<(factor: number) => void>;
  undoRef: React.MutableRefObject<() => void>;
  redoRef: React.MutableRefObject<() => void>;
  saveCurrentProjectRef: React.MutableRefObject<() => Promise<void>>;
  openProjectRef: React.MutableRefObject<() => void | Promise<void>>;
  duplicateLayerRef: React.MutableRefObject<() => void>;
}

/**
 * Registers global keydown/keyup listeners for viewport shortcuts.
 * Uses only refs and getState() — safe to run once at mount.
 */
export function useViewportKeyboard(refs: ViewportKeyboardRefs): void {
  const {
    controlsRef,
    isDrawingRef,
    cameraRef,
    syncCamRef,
    adjustBrushSizeRef,
    undoRef,
    redoRef,
    saveCurrentProjectRef,
    openProjectRef,
    duplicateLayerRef,
  } = refs;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const setOrbitRotate = (enabled: boolean) => {
      if (controlsRef.current) {
        controlsRef.current.enableRotate = enabled;
      }
    };

    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.target instanceof HTMLInputElement || evt.target instanceof HTMLTextAreaElement) return;
      if (evt.key === 'Alt') {
        setOrbitRotate(true);
      }
      if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') {
        if (!isDrawingRef.current && cameraRef.current) {
          const angle = (evt.key === 'ArrowLeft' ? 1 : -1) * THREE.MathUtils.degToRad(10);
          cameraRef.current.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
          cameraRef.current.lookAt(0, 0, 0);
          controlsRef.current?.update();
          syncCamRef.current();
        }
        return;
      }

      if (evt.key === '[') {
        evt.preventDefault();
        adjustBrushSizeRef.current(0.9);
      }
      if (evt.key === ']') {
        evt.preventDefault();
        adjustBrushSizeRef.current(1 / 0.9);
      }
      if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === 'z') {
        evt.preventDefault();
        undoRef.current();
      }
      if ((evt.ctrlKey || evt.metaKey) && (evt.key.toLowerCase() === 'y' || (evt.shiftKey && evt.key.toLowerCase() === 'z'))) {
        evt.preventDefault();
        redoRef.current();
      }
      if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === 's') {
        evt.preventDefault();
        saveCurrentProjectRef.current();
      }
      if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === 'o') {
        evt.preventDefault();
        openProjectRef.current();
      }
      if ((evt.ctrlKey || evt.metaKey) && evt.altKey && evt.key.toLowerCase() === 'n') {
        evt.preventDefault();
        viewportActions.newProject.current();
      }
      if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === 'd') {
        evt.preventDefault();
        duplicateLayerRef.current();
      }
      if ((evt.ctrlKey || evt.metaKey) && (evt.key === 'ArrowUp' || evt.key === 'ArrowDown')) {
        evt.preventDefault();
        const { layers, selectedLayerId, moveLayer } = useAppStore.getState();
        const idx = layers.findIndex((l) => l.id === selectedLayerId);
        if (idx === -1) return;
        const targetIdx = evt.key === 'ArrowUp' ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= layers.length) return;
        viewportActions.pushLayerSnapshot.current();
        moveLayer(idx, targetIdx);
      }
      if (evt.key === ' ') {
        evt.preventDefault();
        const { isPlaying, setIsPlaying } = useAppStore.getState();
        setIsPlaying(!isPlaying);
      }
      if (!evt.ctrlKey && !evt.metaKey && !evt.altKey && !evt.shiftKey && evt.key.toLowerCase() === 'h') {
        evt.preventDefault();
        const { showHotkeyHud, setShowHotkeyHud } = useAppStore.getState();
        setShowHotkeyHud(!showHotkeyHud);
      }
      // Number keys 1–9, 0 → snap to hero positions 0–9
      // Number keys 1–9, 0 → snap to hero positions 0–9
      if (!evt.ctrlKey && !evt.metaKey && !evt.altKey && !evt.shiftKey && /^[0-9]$/.test(evt.key)) {
        const idx = evt.key === '0' ? 9 : parseInt(evt.key, 10) - 1;
        const hero = HERO_POSITIONS[idx];
        if (hero) {
          evt.preventDefault();
          viewportActions.snapToHero.current(hero.id);
        }
      }
      if (evt.key === 'Backspace' && !evt.ctrlKey && !evt.metaKey) {
        if (viewportActions.selectedKfsCount.current === 0) {
          evt.preventDefault();
          if (evt.altKey) {
            viewportActions.clearLayer.current();
          } else {
            viewportActions.deleteSelectedLayer.current();
          }
        }
      }
    };

    const onKeyUp = (evt: KeyboardEvent) => {
      if (evt.key === 'Alt') {
        setOrbitRotate(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);
}
